import React, { FC } from 'react';
import styled from 'styled-components';
import AgeCategorySelector from '../common/AgeCategorySelector';
import LanguageNavigationDropdown from '../../shop/language/navigation/LanguageNavigationDropdown';
import LanguageDropDown from '../../shop/language/navigation/LanguageDropDown';
import LoanFormatDropDown from './LoanFormatDropDown';
import SubNavigationLayout from './SubNavigationLayout';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import TitleDropdown from './TitleDropdown';
import ShopLanguageDropDown from './ShopLanguageDropDown';
import { LoanFormatState } from '../../app-store/loanFormat/loanFormatReducer';
import { mapLanguageDropdownLabel } from '../utils/mapLanguageDropdownLabel';
import { useTranslation } from 'react-i18next';
import RoutingUtils from '../utils/utils-routing';
import { ALL_LANGUAGES } from '../../shop/language/constants';

const StAgeCategorySelector = styled(AgeCategorySelector)`
    margin-left: 10px;
`;

const StForeignLanguageNavigationDropdown = styled(LanguageNavigationDropdown)`
    margin-right: 10px;
`;

const StDropDownContainer = styled.div`
    display: flex;
`;

type Props = {
    readonly loanFormat: LoanFormatState;
    readonly languages: ReadonlyArray<{ readonly key: string; readonly value: string; readonly count: number }>;
    readonly selectedLanguage: string | undefined;
    readonly isInLanguageLearning?: boolean;
    readonly hideGenres?: boolean;
    readonly isSticky: boolean;
    readonly shopLanguage?: string;
};

const ForeignLanguageSubNavigation: FC<Props> = ({
    languages,
    selectedLanguage,
    isInLanguageLearning,
    isSticky,
    shopLanguage
}) => {
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { t } = useTranslation();

    const selectedLanguageObj = selectedLanguage
        ? languages.find(l => l.key === selectedLanguage) ?? {
              key: selectedLanguage,
              value:
                  selectedLanguage === ALL_LANGUAGES
                      ? t('allLanguages')
                      : t(`filters:language.${selectedLanguage}`, selectedLanguage)
          }
        : undefined;

    return (
        <SubNavigationLayout
            left={
                <StDropDownContainer>
                    {shopLanguage ? <ShopLanguageDropDown currentShopLanguage={shopLanguage} /> : null}
                    {isMultiLanguageShopEnabled ? (
                        <TitleDropdown loanFormat={undefined} shopLanguage={shopLanguage} isForeignLanguage />
                    ) : (
                        <StForeignLanguageNavigationDropdown isInLanguageLearning={isInLanguageLearning} />
                    )}

                    {!isMultiLanguageShopEnabled && selectedLanguageObj ? (
                        <LanguageDropDown
                            languages={languages}
                            selectedLanguage={selectedLanguageObj}
                            label={mapLanguageDropdownLabel(t, isMultiLanguageShopEnabled, selectedLanguageObj)}
                            itemTargetBuilder={({
                                language,
                                pathname,
                                isMultiLanguageShopEnabled,
                                ageCategorySearchParam
                            }) =>
                                RoutingUtils.getForeignLanguageUrlWithReplacedLanguage(
                                    language.key,
                                    pathname,
                                    isMultiLanguageShopEnabled,
                                    ageCategorySearchParam
                                )
                            }
                        />
                    ) : null}
                </StDropDownContainer>
            }
            center={
                isMultiLanguageShopEnabled && selectedLanguageObj ? (
                    <LanguageDropDown
                        languages={languages}
                        selectedLanguage={selectedLanguageObj}
                        label={mapLanguageDropdownLabel(t, isMultiLanguageShopEnabled, selectedLanguageObj)}
                        itemTargetBuilder={({
                            language,
                            pathname,
                            isMultiLanguageShopEnabled,
                            ageCategorySearchParam
                        }) =>
                            RoutingUtils.getForeignLanguageUrlWithReplacedLanguage(
                                language.key,
                                pathname,
                                isMultiLanguageShopEnabled,
                                ageCategorySearchParam
                            )
                        }
                    />
                ) : null
            }
            // PORTAL-3993 nav items are currently not shown
            right={
                !isSticky ? (
                    <StDropDownContainer>
                        <LoanFormatDropDown showIcons={false} />
                        <StAgeCategorySelector />
                    </StDropDownContainer>
                ) : null
            }
            isSticky={isSticky}
            width="35"
        />
    );
};

export default ForeignLanguageSubNavigation;
